import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { localizeDate } from '@app/shared/utilities/calendar-date';
import { SelectedRange } from '@app/shared/models/volume-dashboard.model';

interface Preset {
  key: string;
  label: string;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
  standalone: false,
})
export class DateRangePickerComponent implements OnInit {
  @Input() defaultPreset = 'mtd';
  @Output() rangeChange = new EventEmitter<SelectedRange>();
  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;
  @ViewChildren(MatCalendar) private calendars?: QueryList<MatCalendar<Date>>;

  // Reporting data is complete through yesterday.
  private readonly dataEnd = this.addDays(this.today(), -1);

  // Picker edits remain pending until Apply commits them to the dashboard.
  selected = new DateRange<Date>(null, null);
  activePreset = '';
  startAt: Date = this.today();
  private rangeAnchor: Date | null = null;
  private appliedSelection = new DateRange<Date>(null, null);
  private appliedPreset = '';

  readonly presets: Preset[] = [
    { key: 'yesterday', label: 'App.Picker.Presets.Yesterday' },
    { key: 'last7', label: 'App.Picker.Presets.Last7Days' },
    { key: 'last30', label: 'App.Picker.Presets.Last30Days' },
    { key: 'mtd', label: 'App.Picker.Presets.MonthToDate' },
    { key: 'lastMonth', label: 'App.Picker.Presets.LastMonth' },
    { key: 'ytd', label: 'App.Picker.Presets.YearToDate' },
  ];

  get maxDate(): Date { return this.dataEnd; }

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    const start = this.presets.find(p => p.key === this.defaultPreset) ?? this.presets[3];
    this.applyPreset(start);
    this.commitSelection();
    // Avoid changing parent bindings during the current change-detection pass.
    Promise.resolve().then(() => this.emit());
  }

  beginEdit(): void {
    // Each open starts from the last applied range.
    this.restoreAppliedSelection();
    this.syncCalendarMonth();
  }

  onCalendarSelect(date: Date | null): void {
    if (!date) return;
    // One click is a valid single-day range; a second expands from that anchor.
    if (!this.rangeAnchor) {
      this.rangeAnchor = date;
      this.selected = new DateRange(date, date);
    } else {
      const from = date < this.rangeAnchor ? date : this.rangeAnchor;
      const to = date < this.rangeAnchor ? this.rangeAnchor : date;
      this.selected = new DateRange(from, to);
      this.rangeAnchor = null;
    }
    this.activePreset = '';
  }

  applyPreset(preset: Preset): void {
    const [from, to] = this.rangeFor(preset.key);
    this.selected = new DateRange(from, to);
    this.activePreset = preset.key;
    this.rangeAnchor = null;
    this.startAt = new Date(to.getFullYear(), to.getMonth(), 1);
    this.syncCalendarMonth();
  }

  // startAt only affects calendar creation; update activeDate to move an open calendar.
  private syncCalendarMonth(): void {
    const cal = this.calendars?.first;
    if (cal) cal.activeDate = this.startAt;
  }

  apply(): void {
    if (this.selected.start && this.selected.end) {
      this.commitSelection();
      this.emit();
      this.rangeAnchor = null;
      this.menuTrigger?.closeMenu();
    }
  }

  clear(): void {
    this.selected = new DateRange<Date>(null, null);
    this.activePreset = '';
    this.rangeAnchor = null;
  }

  get appliedPresetLabel(): string {
    return this.presets.find(p => p.key === this.appliedPreset)?.label ?? '';
  }

  get rangeText(): string {
    return this.formatRange(this.selected);
  }

  get appliedRangeText(): string {
    return this.formatRange(this.appliedSelection);
  }

  private formatRange(range: DateRange<Date>): string {
    const { start, end } = range;
    if (!start || !end) return '';
    const lang = this.translate.currentLang;
    const md: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const mdy: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const startStr = localizeDate(start, start.getFullYear() === end.getFullYear() ? md : mdy, lang);
    return `${startStr} – ${localizeDate(end, mdy, lang)}`;
  }

  private commitSelection(): void {
    this.appliedSelection = this.copyRange(this.selected);
    this.appliedPreset = this.activePreset;
  }

  private restoreAppliedSelection(): void {
    this.selected = this.copyRange(this.appliedSelection);
    this.activePreset = this.appliedPreset;
    this.rangeAnchor = null;
    if (this.selected.end) {
      this.startAt = new Date(this.selected.end.getFullYear(), this.selected.end.getMonth(), 1);
    }
  }

  private copyRange(range: DateRange<Date>): DateRange<Date> {
    return new DateRange(
      range.start ? new Date(range.start) : null,
      range.end ? new Date(range.end) : null
    );
  }

  private emit(): void {
    const { start, end } = this.selected;
    if (start && end) {
      this.rangeChange.emit({ from: start, to: end, preset: this.activePreset || 'custom', grain: 'day' });
    }
  }

  private rangeFor(key: string): [Date, Date] {
    const end = this.dataEnd;
    const t = this.today();
    switch (key) {
      case 'yesterday': return [end, end];
      case 'last7': return [this.addDays(end, -6), end];
      case 'last30': return [this.addDays(end, -29), end];
      case 'mtd': {
        const start = this.firstOfMonth(t);
        // On the first day of a month, fall back to the latest complete month.
        return start <= end ? [start, end] : [this.firstOfMonth(end), end];
      }
      case 'lastMonth': {
        const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
        return [first, this.lastOfMonth(first)];
      }
      case 'ytd': {
        const start = new Date(t.getFullYear(), 0, 1);
        // January 1 has no completed current-year days, so show the previous year.
        return start <= end ? [start, end] : [new Date(end.getFullYear(), 0, 1), end];
      }
      default: return [this.firstOfMonth(t), end];
    }
  }

  private today(): Date { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  private addDays(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
  private firstOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
  private lastOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
}
